import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, HeartHandshake, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { txt } from "@/lib/site";

const inputCls = "w-full border border-slate-200 rounded-xl p-3.5 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 bg-white transition";
const TYPES = ["Doa", "Konseling", "Kedukaan", "Kunjungan Kasih", "Pengucapan Syukur"];

export default function PrayerRequest() {
  const [site, setSite] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "", type: "Doa", message: "" });
  const [confidential, setConfidential] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => { api.get("/public/site").then((r) => setSite(r.data)).catch(() => {}); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/public/pastoral", {
        ...form,
        message: confidential ? `[RAHASIA] ${form.message}` : form.message,
      });
      setDone(true);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white px-5 py-12 md:py-20" data-testid="public-prayer-page">
      <div className="absolute inset-0 bg-[radial-gradient(900px_circle_at_30%_0%,rgba(37,99,235,.28),transparent_55%)] pointer-events-none" />
      <div className="relative max-w-xl mx-auto">
        <p className="text-[11px] font-black uppercase tracking-[0.4em] text-amber-300">GIM Church Makassar</p>
        <h1 className="mt-4 text-4xl sm:text-5xl font-black tracking-tighter leading-[0.95]">{txt(site, "welcome_prayer_title", "Pokok Doa Pribadi")}</h1>
        <p className="mt-4 text-white/60">{txt(site, "welcome_prayer_text", "Kirim pokok doa Anda, tim pastoral akan mendoakan secara pribadi.")}</p>

        {done ? (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mt-10 rounded-3xl bg-white/5 border border-white/10 p-8 text-center" data-testid="prayer-success">
            <span className="w-16 h-16 mx-auto rounded-3xl bg-emerald-500/15 text-emerald-300 grid place-items-center"><CheckCircle2 className="w-8 h-8" /></span>
            <p className="mt-5 text-xl font-extrabold">Pokok doa terkirim</p>
            <p className="mt-2 text-sm text-white/60">Tuhan Yesus mendengar doamu. Tim pastoral GIM Church akan mendoakan dan menghubungi Anda bila perlu.</p>
            <button onClick={() => { setDone(false); setForm({ name: "", phone: "", type: "Doa", message: "" }); setConfidential(false); }}
              data-testid="prayer-again-button" className="mt-6 text-sm font-bold bg-white text-slate-900 rounded-full px-6 py-3 active:scale-95 transition">Kirim pokok doa lain</button>
          </motion.div>
        ) : (
          <form onSubmit={submit} className="mt-10 bg-white rounded-3xl p-6 md:p-8 space-y-4 text-slate-800 shadow-2xl" data-testid="prayer-form">
            <input className={inputCls} placeholder="Nama" value={form.name} onChange={set("name")} required data-testid="prayer-name-input" />
            <input className={inputCls} placeholder="No. WhatsApp" value={form.phone} onChange={set("phone")} required data-testid="prayer-phone-input" />
            <select className={inputCls} value={form.type} onChange={set("type")} data-testid="prayer-type-select">{TYPES.map((t) => <option key={t}>{t}</option>)}</select>
            <textarea rows={5} className={inputCls} placeholder="Tuliskan pokok doa Anda…" value={form.message} onChange={set("message")} required data-testid="prayer-message-input" />
            <label className="flex items-center gap-2 text-sm font-semibold text-slate-600">
              <input type="checkbox" checked={confidential} onChange={(e) => setConfidential(e.target.checked)} data-testid="prayer-confidential-check" />
              <Lock className="w-3.5 h-3.5" /> Bersifat rahasia (hanya gembala/pastoral)
            </label>
            <button disabled={loading} data-testid="prayer-submit-button" className="w-full inline-flex items-center justify-center gap-2 bg-blue-600 text-white font-bold py-4 rounded-xl hover:bg-blue-700 active:scale-95 transition disabled:opacity-60">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><HeartHandshake className="w-4 h-4" /> Kirim Pokok Doa</>}
            </button>
          </form>
        )}
        <Link to="/" className="mt-8 inline-block text-sm font-bold text-white/50 hover:text-white transition" data-testid="prayer-home-link">← Kembali ke beranda</Link>
      </div>
    </div>
  );
}
